import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Icon } from "../components/icons";
import * as work from "../lib/workClient";
import type { PortalView } from "../lib/types";

const bandColor = (b: string) => (b === "good" ? "var(--good)" : b === "watch" ? "var(--watch)" : "var(--risk)");
const OBJ_LABEL: Record<string, string> = { todo: "To do", in_progress: "In progress", done: "Done", blocked: "Blocked" };
const OBJ_COLOR: Record<string, string> = { todo: "var(--faint)", in_progress: "var(--watch)", done: "var(--good)", blocked: "var(--risk)" };
const shortDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function Portal() {
  const { token } = useParams<{ token: string }>();
  const [view, setView] = useState<PortalView | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing">("loading");

  useEffect(() => {
    if (!token) return;
    work.loadPortal(token).then((v) => { setView(v); setState(v ? "ready" : "missing"); });
  }, [token]);

  if (state === "loading") return <div className="portal-wrap"><div className="portal-card"><p className="faint">Loading…</p></div></div>;
  if (state === "missing" || !view) {
    return (
      <div className="portal-wrap">
        <div className="portal-card" style={{ textAlign: "center" }}>
          <BrandMark />
          <h2 style={{ fontFamily: "var(--serif)", fontSize: 22, marginTop: 16 }}>This link isn't active</h2>
          <p className="faint" style={{ marginTop: 8 }}>Ask your Customer Success Manager for a fresh link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-wrap">
      <div className="portal-card">
        <div className="row between" style={{ alignItems: "flex-start" }}>
          <BrandMark />
          <span className="badge" style={{ color: bandColor(view.status.band), borderColor: "currentColor" }}>{view.status.label}</span>
        </div>

        <p className="eyebrow" style={{ marginTop: 22 }}>Customer success review</p>
        <h1 style={{ fontFamily: "var(--serif)", fontSize: 30, margin: "4px 0 6px" }}>{view.accountName}</h1>
        <p className="faint" style={{ fontSize: 13.5 }}>Prepared by {view.csmName} · {shortDate(view.generatedAt)}</p>

        <div className="portal-stats">
          <div className="portal-stat">
            <div className="portal-stat__label">Seat adoption</div>
            <div className="portal-stat__value">{view.adoption.utilization}%</div>
            <div className="faint" style={{ fontSize: 12 }}>{view.adoption.seatsActive} of {view.adoption.seats} active</div>
          </div>
          <div className="portal-stat">
            <div className="portal-stat__label">Renewal</div>
            <div className="portal-stat__value" style={{ fontSize: 20 }}>{shortDate(view.renewalDate)}</div>
            <div className="faint" style={{ fontSize: 12 }}>Upcoming term</div>
          </div>
          <div className="portal-stat">
            <div className="portal-stat__label">Satisfaction (NPS)</div>
            <div className="portal-stat__value" style={{ color: view.nps === null ? "var(--ink)" : view.nps >= 30 ? "var(--good)" : "var(--watch)" }}>{view.nps === null ? "—" : `${view.nps > 0 ? "+" : ""}${view.nps}`}</div>
            <div className="faint" style={{ fontSize: 12 }}>Your latest feedback</div>
          </div>
        </div>

        {view.successPlan && (
          <section className="portal-section">
            <h3>Success plan · {view.successPlan.title}</h3>
            <p className="faint" style={{ fontSize: 13.5, marginBottom: 12 }}>{view.successPlan.outcome}</p>
            <div style={{ display: "grid", gap: 8 }}>
              {view.successPlan.objectives.map((o, i) => (
                <div key={i} className="row between portal-obj">
                  <span style={{ fontSize: 13.5 }}>{o.title}</span>
                  <span className="badge" style={{ color: OBJ_COLOR[o.status] ?? "var(--faint)", borderColor: "currentColor" }}>{OBJ_LABEL[o.status] ?? o.status}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {view.wins.length > 0 && (
          <section className="portal-section">
            <h3><Icon name="check" size={15} /> Recent wins</h3>
            <ul className="portal-list">{view.wins.map((w, i) => <li key={i}><span className="portal-dot" style={{ background: "var(--good)" }} />{w}</li>)}</ul>
          </section>
        )}

        {view.focusAreas.length > 0 && (
          <section className="portal-section">
            <h3><Icon name="target" size={15} /> Where we're focused</h3>
            <ul className="portal-list">{view.focusAreas.map((f, i) => <li key={i}><span className="portal-dot" style={{ background: "var(--corona)" }} />{f}</li>)}</ul>
          </section>
        )}

        <footer className="portal-foot">
          <span className="faint" style={{ fontSize: 12 }}>Shared securely via Penumbrai · Read-only</span>
        </footer>
      </div>
    </div>
  );
}

function BrandMark() {
  return (
    <div className="row" style={{ gap: 10, alignItems: "center" }}>
      <svg viewBox="0 0 40 40" width="30" height="30">
        <circle cx="20" cy="20" r="11" fill="#0C0D13" stroke="#C9821A" strokeWidth="1.6" />
        <circle cx="24.5" cy="16" r="11" fill="#0C0D13" />
      </svg>
      <b style={{ fontFamily: "var(--serif)", fontSize: 18 }}>Penumbrai</b>
    </div>
  );
}
